/* ============================================================================
 * printable-gauge / index.js   — engine entry point & orchestrator
 *
 * window.PrintableGauge.mount(slug, config) builds a complete dual-mode gauge
 * tool inside the page's [data-gauge-root] element:
 *   - SCREEN mode: calibrate px/mm against an ID-1 card, then lay the object
 *     on calibrated on-screen rulers.
 *   - PRINT mode:  download a pdf-lib vector PDF that prints 1:1, with a 50 mm
 *     verification bar.
 *
 * The heavy logic lives in this SW-cached module; the tool blade only ships a
 * tiny static config object + one mount() call (PageCache-safe).
 *
 * config = {
 *   slug, dataset, measure:'linear-1axis'|'linear-2axis',
 *   i18n:{...all UI strings...}, paper:['a4','letter']
 * }
 * ========================================================================== */

import { createCalibration } from './calibration.js';
import { createLinearAxis, rankRows, fmtMm, fmtInchFrac } from './measure.js';
import { renderSVG } from './render.js';
import { buildPdf, PAPER } from './pdf.js';

const MARGIN_MM = 14;
const UNIT_KEY = 'printable-gauge-unit';   // shared across every gauge tool

/* ---- telemetry funnel --------------------------------------------------- */
function makeTelemetry(slug) {
    const fired = {};
    const send = (fn, ...a) => { try { if (typeof window[fn] === 'function') window[fn](...a); } catch (e) {} };
    return {
        event: (action, extra) => send('__ae', 'feature_use', Object.assign({ slug, action }, extra || {})),
        funnel: (step, extra) => {
            if (fired[step]) return;            // each funnel step once per session
            fired[step] = 1;
            send('__ae', 'feature_use', Object.assign({ slug, action: 'funnel', step }, extra || {}));
        },
        error: (msg, ctx) => send('__aeToolError', { slug, msg: String(msg), ctx }),
    };
}

/* ---- small DOM helpers -------------------------------------------------- */
function elem(tag, cls, text) {
    const e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text != null) e.textContent = text;
    return e;
}

function mount(slug, config) {
    const root = document.querySelector('[data-gauge-root]');
    if (!root) return { destroy() {} };
    const i18n = config.i18n || {};
    const tel = makeTelemetry(slug);

    let dataset = null;
    let calibration = null;
    const axes = [];
    let resultCard = null;
    let candsCard = null;
    let detailCard = null;
    let selectedId = null;          // candidate the user has clicked to inspect

    // mm / inch readout unit — shared & persisted across all gauge tools.
    const TS = window.__toolSettings || null;
    let unit = 'mm';
    if (TS) { try { if (TS.load(UNIT_KEY, 'mm') === 'in') unit = 'in'; } catch (e) {} }
    const unitFmt = () => (unit === 'in' ? fmtInchFrac : (mm) => fmtMm(mm));

    root.innerHTML = '';
    root.classList.add('pg-root');

    /* ---- modes: a tool may offer screen + print, or be print-only ---- */
    const modes = (config.modes && config.modes.length) ? config.modes : ['screen', 'print'];
    const hasScreen = modes.indexOf('screen') >= 0;
    const hasPrint = modes.indexOf('print') >= 0;

    const panelScreen = elem('div', 'pg-panel');
    const panelPrint = elem('div', 'pg-panel pg-hidden');
    let tabScreen = null, tabPrint = null;

    function showMode(m) {
        if (m === 'screen' && !hasScreen) m = 'print';
        if (m === 'print' && !hasPrint) m = 'screen';
        const screen = m === 'screen';
        if (tabScreen) tabScreen.classList.toggle('is-active', screen);
        if (tabPrint) tabPrint.classList.toggle('is-active', !screen);
        if (hasScreen) panelScreen.classList.toggle('pg-hidden', !screen);
        if (hasPrint) panelPrint.classList.toggle('pg-hidden', screen);
        tel.event('mode_select', { mode: m });
    }

    // Tabs only when both modes are available.
    if (hasScreen && hasPrint) {
        const tabs = elem('div', 'pg-tabs');
        tabScreen = elem('button', 'pg-tab', i18n.mode_screen);
        tabPrint = elem('button', 'pg-tab', i18n.mode_print);
        tabScreen.type = tabPrint.type = 'button';
        tabScreen.addEventListener('click', () => showMode('screen'));
        tabPrint.addEventListener('click', () => showMode('print'));
        tabs.appendChild(tabScreen);
        tabs.appendChild(tabPrint);
        root.appendChild(tabs);
    }

    if (hasScreen) root.appendChild(panelScreen);
    if (hasPrint) root.appendChild(panelPrint);

    /* ====================================================================
     * SCREEN MODE
     * ================================================================== */
    let lastResult = null;
    let lastCandidates = [];
    function recompute() {
        if (!dataset || !dataset.identify) return;
        const values = {};
        axes.forEach(a => { values[a.key] = a.axis.getMm(); });
        const res = dataset.identify(values, i18n);
        lastResult = res;
        // Generic ranked candidate list — works for every dataset (rows + axes).
        lastCandidates = rankRows(dataset.rows || [], values, dataset.axes || [], 6);
        // Keep the inspected selection valid; default to the closest candidate.
        if (!lastCandidates.some(c => c.row.id === selectedId)) {
            selectedId = lastCandidates.length ? lastCandidates[0].row.id : null;
        }
        renderResult(res);
        renderCandidates();
        renderDetail();
        if (res) tel.funnel('identify', { d: res.confidenceDeltaMm != null ? +res.confidenceDeltaMm.toFixed(2) : null });
    }

    function selectCandidate(id) {
        if (id === selectedId) return;
        selectedId = id;
        renderCandidates();
        renderDetail();
        tel.event('candidate_select', { id: String(id) });
    }

    /* short per-axis glyph for the dense candidate specs row */
    function axisGlyph(key) {
        if (/dia/i.test(key)) return 'ø';
        if (/across_flats|^af$/i.test(key)) return 'AF';
        if (/length|len|height|long/i.test(key)) return 'L';
        if (/pitch/i.test(key)) return 'P';
        return key.slice(0, 2);
    }

    /* ---- candidate list (all tools) ---- */
    function renderCandidates() {
        if (!candsCard) return;
        candsCard.innerHTML = '';
        const cands = lastCandidates;
        if (!cands.length) { candsCard.classList.add('pg-hidden'); return; }
        candsCard.classList.remove('pg-hidden');

        const head = elem('div', 'pg-card-head');
        const htext = elem('div', 'pg-card-head-text');
        htext.appendChild(elem('div', 'pg-card-head-title', i18n.candidates_title || 'Candidates'));
        const good = cands.filter(c => c.score >= 60).length;
        htext.appendChild(elem('div', 'pg-card-head-sub',
            (i18n.candidates_sub || '%n likely · sorted by closeness').replace('%n', good)));
        head.appendChild(htext);
        candsCard.appendChild(head);

        const list = elem('div', 'pg-cand-list');
        cands.forEach((c, i) => {
            const row = elem('button', 'pg-cand-row');
            row.type = 'button';
            if (String(c.row.id) === String(selectedId)) row.classList.add('is-selected');
            if (i === 0) row.classList.add('is-best');

            const gl = elem('span', 'pg-cand-glyph');
            if (c.row.kind) gl.setAttribute('data-kind', c.row.kind);
            row.appendChild(gl);

            const info = elem('div', 'pg-cand-info');
            const nm = elem('div', 'pg-cand-name');
            nm.appendChild(elem('span', 'pg-cand-label', c.row.label));
            (c.row.aliases || []).slice(0, 2).forEach(a => nm.appendChild(elem('span', 'pg-alias', a)));
            info.appendChild(nm);

            // Reference dims stay in mm (standard catalog units); only the live
            // measurement readout follows the mm/in toggle.
            const specs = elem('div', 'pg-cand-specs');
            (dataset.axes || []).forEach(ax => {
                const rv = c.row[ax.matchField];
                if (typeof rv !== 'number') return;
                const it = elem('span', 'pg-cand-spec' + (c.axisMatch && c.axisMatch[ax.key] ? ' is-match' : ''));
                it.appendChild(elem('span', 'pg-spec-gl', axisGlyph(ax.key)));
                it.appendChild(elem('strong', null, rv.toFixed(1)));
                specs.appendChild(it);
            });
            if (c.row.volts) {
                const v = elem('span', 'pg-cand-spec');
                v.appendChild(elem('strong', null, String(c.row.volts)));
                specs.appendChild(v);
            }
            info.appendChild(specs);
            row.appendChild(info);

            const pct = elem('div', 'pg-cand-pct' + (c.score >= 85 ? '' : c.score >= 60 ? ' is-fair' : ' is-poor'));
            pct.textContent = c.score + '%';
            row.appendChild(pct);

            row.addEventListener('click', () => selectCandidate(c.row.id));
            list.appendChild(row);
        });
        candsCard.appendChild(list);
    }

    /* ---- rich detail card (only datasets that expose detail(): batteries) ---- */
    function renderDetail() {
        if (!detailCard) return;
        detailCard.innerHTML = '';
        if (typeof dataset.detail !== 'function' || selectedId == null) {
            detailCard.classList.add('pg-hidden'); return;
        }
        const cand = lastCandidates.find(c => String(c.row.id) === String(selectedId));
        const det = cand ? dataset.detail(cand.row, i18n) : null;
        if (!det) { detailCard.classList.add('pg-hidden'); return; }
        detailCard.classList.remove('pg-hidden');

        const head = elem('div', 'pg-detail-head');
        head.appendChild(elem('div', 'pg-detail-title', cand.row.label));
        if (det.aliases && det.aliases.length) {
            const al = elem('div', 'pg-detail-aliases');
            det.aliases.forEach(a => al.appendChild(elem('span', 'pg-alias', a)));
            head.appendChild(al);
        }
        detailCard.appendChild(head);

        if (det.fields && det.fields.length) {
            const grid = elem('div', 'pg-detail-grid');
            det.fields.forEach(f => {
                const cell = elem('div', 'pg-detail-cell');
                cell.appendChild(elem('div', 'pg-detail-cell-lbl', f.label));
                cell.appendChild(elem('div', 'pg-detail-cell-val', f.value));
                grid.appendChild(cell);
            });
            detailCard.appendChild(grid);
        }

        if (det.uses && det.uses.length) {
            const sec = elem('div', 'pg-detail-uses');
            sec.appendChild(elem('div', 'pg-detail-uses-title', i18n.uses_title || 'Common uses'));
            const tags = elem('div', 'pg-uses');
            det.uses.forEach(u => tags.appendChild(elem('span', 'pg-use', u)));
            sec.appendChild(tags);
            detailCard.appendChild(sec);
        }

        if (det.warning) {
            const w = elem('div', 'pg-warning');
            w.appendChild(elem('span', 'pg-warning-ico', '⚠'));
            w.appendChild(elem('div', 'pg-warning-text', det.warning));
            detailCard.appendChild(w);
        }
    }

    function renderResult(res) {
        resultCard.innerHTML = '';
        const head = elem('div', 'pg-result-head', i18n.result_title);
        resultCard.appendChild(head);
        if (!res) {
            resultCard.appendChild(elem('p', 'pg-result-empty', i18n.result_empty));
            return;
        }
        // confidence chip
        const d = res.confidenceDeltaMm;
        let chipCls = 'pg-chip', chipTxt = i18n.result_confident;
        if (d == null || d > 0.5) { chipCls += ' is-warn'; chipTxt = i18n.result_ambiguous; }
        else if (d > 0.2) { chipCls += ' is-mid'; chipTxt = i18n.result_approx; }
        else { chipCls += ' is-ok'; }
        const big = elem('div', 'pg-result-big');
        big.appendChild(elem('span', 'pg-result-title', res.title));
        big.appendChild(elem('span', chipCls, chipTxt));
        resultCard.appendChild(big);

        const list = elem('dl', 'pg-result-fields');
        (res.fields || []).forEach(f => {
            const dt = elem('dt', null, f.label);
            const dd = elem('dd', f.primary ? 'is-primary' : null, f.value);
            list.appendChild(dt); list.appendChild(dd);
        });
        resultCard.appendChild(list);
        if (res.note) resultCard.appendChild(elem('p', 'pg-result-note', res.note));
    }

    function buildUnitToggle() {
        const seg = elem('div', 'pg-unit-seg');
        const bm = elem('button', 'pg-unit-btn' + (unit === 'mm' ? ' is-active' : ''), i18n.unit_mm || 'mm');
        const bi = elem('button', 'pg-unit-btn' + (unit === 'in' ? ' is-active' : ''), i18n.unit_in || 'inch');
        bm.type = bi.type = 'button';
        function set(u) {
            if (u === unit) return;
            unit = u;
            bm.classList.toggle('is-active', u === 'mm');
            bi.classList.toggle('is-active', u === 'in');
            axes.forEach(a => a.axis.setFormat(unitFmt()));
            if (TS) { try { TS.save(UNIT_KEY, u); } catch (e) {} }
            tel.event('unit_select', { unit: u });
        }
        bm.addEventListener('click', () => set('mm'));
        bi.addEventListener('click', () => set('in'));
        seg.appendChild(bm); seg.appendChild(bi);
        return seg;
    }

    function buildScreenPanel() {
        // calibration (full width)
        calibration = createCalibration(i18n);
        if (calibration.reusedSavedCalibration) tel.event('calib_reused');
        const calibUI = calibration.buildUI(() => {
            axes.forEach(a => a.axis.relayout());
            tel.funnel('calibrate');
        });
        panelScreen.appendChild(calibUI);

        // measurement stage (full width — the rulers need horizontal room)
        const stage = elem('div', 'pg-stage');
        const stageTop = elem('div', 'pg-stage-top');
        stageTop.appendChild(elem('p', 'pg-stage-intro', i18n.screen_intro));
        stageTop.appendChild(buildUnitToggle());
        stage.appendChild(stageTop);
        (dataset.axes || []).forEach(spec => {
            const axis = createLinearAxis({
                lengthMm: spec.lengthMm,
                calibration,
                i18n,
                format: unitFmt(),
                label: i18n[spec.i18nKey] || spec.key,
                onChange: () => { tel.funnel('measure'); recompute(); },
            });
            axes.push({ key: spec.key, axis });
            stage.appendChild(axis.el);
        });
        panelScreen.appendChild(stage);

        // results: headline verdict + ranked candidate list share a responsive grid
        const grid = elem('div', 'pg-screen-grid');
        resultCard = elem('div', 'pg-result');
        candsCard = elem('div', 'pg-cards pg-cands pg-hidden');
        grid.appendChild(resultCard);
        grid.appendChild(candsCard);
        panelScreen.appendChild(grid);

        // rich detail card (full width below; only datasets exposing detail())
        detailCard = elem('div', 'pg-cards pg-detail pg-hidden');
        panelScreen.appendChild(detailCard);

        renderResult(null);
    }

    /* ====================================================================
     * PRINT MODE
     * ================================================================== */
    function sheetPrimitives(paperKey) {
        const paper = PAPER[paperKey] || PAPER.a4;
        return dataset.sheet({
            i18n,
            contentWmm: paper.w - 2 * MARGIN_MM,
            contentHmm: paper.h - 2 * MARGIN_MM,
        });
    }

    function buildPrintPanel() {
        let paperKey = (config.paper && config.paper[0]) || 'a4';

        panelPrint.appendChild(elem('p', 'pg-stage-intro', i18n.print_intro));

        // paper selector
        const paperRow = elem('div', 'pg-paper-row');
        (config.paper || ['a4', 'letter']).forEach(pk => {
            const b = elem('button', 'pg-paper-btn' + (pk === paperKey ? ' is-active' : ''),
                i18n['paper_' + pk] || (PAPER[pk] ? PAPER[pk].label : pk));
            b.type = 'button';
            b.addEventListener('click', () => {
                paperKey = pk;
                paperRow.querySelectorAll('.pg-paper-btn').forEach(x => x.classList.remove('is-active'));
                b.classList.add('is-active');
                renderPreview();
            });
            paperRow.appendChild(b);
        });
        panelPrint.appendChild(paperRow);

        // preview
        const preview = elem('div', 'pg-preview');
        panelPrint.appendChild(preview);
        function renderPreview() {
            preview.innerHTML = '';
            try {
                const paper = PAPER[paperKey] || PAPER.a4;
                const svg = renderSVG(sheetPrimitives(paperKey), {
                    wMm: paper.w - 2 * MARGIN_MM,
                    hMm: paper.h - 2 * MARGIN_MM,
                    pxPerMm: 1.9, bg: '#ffffff',
                });
                svg.classList.add('pg-preview-svg');
                preview.appendChild(svg);
            } catch (e) {
                tel.error(e.message, 'preview');
                preview.appendChild(elem('p', 'pg-result-empty', i18n.err_generic));
            }
        }
        renderPreview();

        // actions
        const actions = elem('div', 'pg-actions');
        const dlBtn = elem('button', 'pg-btn pg-dl-btn', i18n.btn_download);
        dlBtn.type = 'button';
        const printBtn = elem('button', 'pg-btn-ghost pg-print-btn', i18n.btn_print);
        printBtn.type = 'button';
        actions.appendChild(dlBtn);
        actions.appendChild(printBtn);
        panelPrint.appendChild(actions);
        panelPrint.appendChild(elem('p', 'pg-print-note', i18n.print_note));

        async function makePdf() {
            const blob = await buildPdf(sheetPrimitives(paperKey), {
                paper: paperKey, marginMm: MARGIN_MM, title: 'Timbrica — ' + slug,
            });
            return blob;
        }

        dlBtn.addEventListener('click', async () => {
            dlBtn.disabled = true;
            const prev = dlBtn.textContent;
            dlBtn.textContent = i18n.downloading || '…';
            try {
                const blob = await makePdf();
                const name = slug + '-' + paperKey + '.pdf';
                if (typeof window.__downloadFile === 'function') window.__downloadFile(blob, name);
                else {
                    const a = document.createElement('a');
                    a.href = URL.createObjectURL(blob); a.download = name; a.click();
                }
                tel.funnel('export', { via: 'download', paper: paperKey });
            } catch (e) {
                tel.error(e.message, 'pdf');
                if (typeof window.__toast === 'function') window.__toast(i18n.err_pdf || i18n.err_generic, 'error');
            } finally {
                dlBtn.disabled = false;
                dlBtn.textContent = prev;
            }
        });

        printBtn.addEventListener('click', async () => {
            printBtn.disabled = true;
            try {
                const blob = await makePdf();
                const url = URL.createObjectURL(blob);
                const ifr = document.createElement('iframe');
                ifr.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0';
                ifr.src = url;
                document.body.appendChild(ifr);
                ifr.onload = () => {
                    try { ifr.contentWindow.focus(); ifr.contentWindow.print(); }
                    catch (e) { window.open(url, '_blank'); }
                    setTimeout(() => { ifr.remove(); URL.revokeObjectURL(url); }, 60000);
                };
                tel.funnel('export', { via: 'print', paper: paperKey });
            } catch (e) {
                tel.error(e.message, 'print');
                if (typeof window.__toast === 'function') window.__toast(i18n.err_pdf || i18n.err_generic, 'error');
            } finally {
                printBtn.disabled = false;
            }
        });
    }

    /* ---- load the dataset, then build both panels ---- */
    import('./data/' + config.dataset + '.js')
        .then(mod => {
            dataset = mod.default || mod;
            if (hasScreen) buildScreenPanel();
            if (hasPrint) buildPrintPanel();
            showMode(modes[0]);

            // Test hook — lets the Playwright smoke drive the tool deterministically.
            window.__pgDebug = {
                slug,
                showMode,
                setAxis: (key, mm) => {
                    const a = axes.find(x => x.key === key);
                    if (a) { a.axis.setMm(mm); recompute(); }
                    return a ? a.axis.getMm() : null;
                },
                getResult: () => lastResult,
                getCandidates: () => lastCandidates,
                selectCandidate: (id) => { selectCandidate(id); return selectedId; },
                getUnit: () => unit,
                pxPerMm: () => calibration && calibration.pxPerMm,
            };
            tel.event('ready');
        })
        .catch(e => {
            tel.error(e.message, 'dataset');
            root.appendChild(elem('p', 'pg-result-empty', i18n.err_generic));
        });

    return { destroy() { root.innerHTML = ''; } };
}

/* ---- expose + drain any queued mount() calls --------------------------- */
window.PrintableGauge = { mount };
const q = window.__PG_QUEUE;
if (q && q.length) q.splice(0).forEach(args => { try { mount(args[0], args[1]); } catch (e) {} });
window.dispatchEvent(new Event('printable-gauge:ready'));
